import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { appVersionService, VersionCheckResult, WEB_VERSION } from './services/appVersion';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Dashboard } from './pages/Dashboard';
import { Kitchens } from './pages/Kitchens';
import { Items } from './pages/Items';
import { Tables } from './pages/Tables';
import { Orders } from './pages/Orders';
import { PlaceOrder } from './pages/PlaceOrder';
import { Reports } from './pages/Reports';
import { Customers } from './pages/Customers';
import { Profile } from './pages/Profile';
import { Settings } from './pages/Settings';
import { OrganizationOnboarding } from './pages/OrganizationOnboarding';
import { OrganizationDetail } from './pages/OrganizationDetail';
import { EmployeeManagement } from './pages/EmployeeManagement';
import { RolePageConfig } from './pages/RolePageConfig';
import { TableOrders } from './pages/TableOrders';
import { Notifications } from './pages/Notifications';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const [updateInfo, setUpdateInfo] = useState<VersionCheckResult | null>(null);
  const [versionChecked, setVersionChecked] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    appVersionService.check().then(result => {
      setUpdateInfo(result);
      setVersionChecked(true);
    });
  }, []);

  if (!versionChecked) return null;

  // forceUpdate=true → full blocking screen, no way around it
  if (updateInfo?.forceUpdate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-indigo-50 p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-6">
            <img src="/logo192.png" alt="Restro360" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-3">Update Required</h1>
          <p className="text-gray-500 text-sm leading-relaxed mb-4">{updateInfo.message}</p>
          <p className="text-xs text-gray-400 mb-2">Your version: <span className="text-red-400 font-semibold">{WEB_VERSION}</span></p>
          <p className="text-xs text-gray-400 mb-8">Required version: <span className="text-green-500 font-semibold">{updateInfo.latestVersion}</span></p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors text-sm"
          >
            Refresh Page
          </button>
          {updateInfo.downloadUrl && (
            <a
              href={updateInfo.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="block mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Download latest version →
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
          <div className="App">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              {/* Public QR self-ordering page — no auth required */}
              <Route path="/table-orders/:restaurantId/:tableId" element={<TableOrders />} />
              
              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="kitchens" element={<Kitchens />} />
                <Route path="items" element={<Items />} />
                <Route path="tables" element={<Tables />} />
                <Route path="customers" element={<Customers />} />
                <Route path="orders" element={<Orders />} />
                <Route path="place-order" element={<PlaceOrder />} />
                <Route path="reports" element={<Reports />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
                <Route path="organization-onboarding" element={<OrganizationOnboarding />} />
                <Route path="organization-onboarding/:orgId" element={<OrganizationDetail />} />
                <Route path="employee-management" element={<EmployeeManagement />} />
                <Route path="role-page-config" element={<RolePageConfig />} />
                <Route path="notifications" element={<Notifications />} />
              </Route>
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Soft update banner — dismissible */}
            {updateInfo && !updateInfo.forceUpdate && !bannerDismissed && (
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[300] flex items-center gap-3 bg-indigo-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-medium max-w-sm w-full">
                <span className="flex-1">🚀 {updateInfo.message} <span className="opacity-70 text-xs">(v{updateInfo.latestVersion})</span></span>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-white text-indigo-600 font-bold px-3 py-1 rounded-lg text-xs hover:bg-indigo-50 transition-colors flex-shrink-0"
                >
                  Refresh
                </button>
                <button
                  onClick={() => setBannerDismissed(true)}
                  className="text-indigo-200 hover:text-white transition-colors flex-shrink-0 text-lg leading-none"
                >
                  ✕
                </button>
              </div>
            )}

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#22c55e',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 4000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;